import { Resource } from '../resources';
import axios, { AxiosPromise, AxiosRequestConfig, AxiosResponse } from 'axios';
import { api } from '../apiClient';
import { store } from '../../../apps/legacy-app/src/store';
import { selectIdentityCompanyId } from '../../../apps/legacy-app/src/store/entities/identity';
import { apiMessagesStore } from '../utils/ApiMessagesStore';
import { apiLoadingStore } from '../utils/ApiLoadingStore';
import { confirmationService } from '../utils/ConfirmationService';
import { Page } from '../models/common/Page';

const BASE_URL = import.meta.env.VITE_API_URL as string;
export type Args = Record<string, unknown>;
export type AxiosConfig<D = any> = AxiosRequestConfig<D> & {
  silentSuccess?: boolean;
  /**
   * A refresh behind a screen that already shows the resource: its loading and error state are left alone (so the screen keeps its content
   * and unsaved input), and a failure is only reported as a snackbar.
   */
  background?: boolean;
};
export type AxiosPagePromise<D> = AxiosPromise<Page<D>>;

type RestMethodType = 'get' | 'put' | 'patch' | 'post' | 'delete';

export class BaseService {
  protected async get<T>(resource: Resource, args?: Args, options: AxiosConfig = {}): AxiosPromise<T> {
    return this.makeRequest<T>('get', resource, undefined, args, options);
  }

  protected async put<T>(resource: Resource, body?: any, args?: Args, options: AxiosConfig = {}): AxiosPromise<T> {
    return this.makeRequest<T>('put', resource, body, args, options);
  }

  protected async patch<T>(resource: Resource, body?: any, args?: Args, options: AxiosConfig = {}): AxiosPromise<T> {
    return this.makeRequest<T>('patch', resource, body, args, options);
  }

  protected async post<T>(resource: Resource, body?: any, args?: Args, options: AxiosConfig = {}): AxiosPromise<T> {
    return this.makeRequest<T>('post', resource, body, args, options);
  }

  protected async delete<T>(resource: Resource, args?: Args, options: AxiosConfig = {}): AxiosPromise<T> {
    // Show confirmation modal before delete, unless the resource opts out (e.g. the caller already confirms)
    if (!resource.disableDeleteConfirmation) {
      const confirmed = await confirmationService.confirmDelete({
        title: resource.deleteConfirmationTitleKey,
        message: resource.deleteConfirmationMessage,
        confirmButtonKey: resource.deleteConfirmationButtonKey,
        cancelButtonKey: resource.deleteConfirmationCancelButtonKey,
      });

      if (!confirmed) {
        const cancelledResponse = {
          data: null as any,
          status: 0,
          statusText: 'Cancelled',
          headers: {},
          config: {} as any,
          cancelled: true,
        };
        return cancelledResponse as AxiosResponse<T>;
      }
    }

    return this.makeRequest<T>('delete', resource, undefined, args, options);
  }

  private getUrl(resource: Resource, args?: Args) {
    let path = resource.path;
    if (path.includes('{companyId}') && !args?.companyId) {
      const companyId = selectIdentityCompanyId(store.getState());
      path = path.replace('{companyId}', String(companyId));
    }
    if (args) {
      Object.entries(args).forEach(([key, value]) => {
        if (path.includes(`{${key}}`)) {
          path = path.replace(`{${key}}`, String(value));
          delete args[key]; // Remove the key to avoid sending it as a query parameter
        }
      });
    }

    return BASE_URL + (resource.version && '/api/' + resource.version) + path;
  }
  private async makeRequest<T>(
    method: RestMethodType,
    resource: Resource,
    body?: any,
    args?: Args,
    options: AxiosConfig = {},
  ): AxiosPromise<T> {
    if (!options.background) apiLoadingStore.startLoading(resource);

    try {
      const url = this.getUrl(resource, args);
      const config = {
        params: this.processParams(args),
        paramsSerializer: {
          indexes: null, // This produces repeat format: sort=val1&sort=val2
        },
        ...options,
      };

      let response: AxiosResponse<T>;
      switch (method) {
        case 'get':
          response = await api.get<T>(url, config);
          break;
        case 'put':
          response = await api.put<T>(url, body, config);
          break;
        case 'patch':
          response = await api.patch<T>(url, body, config);
          break;
        case 'post':
          response = await api.post<T>(url, body, config);
          break;
        case 'delete':
          response = await api.delete<T>(url, config);
          break;
      }

      if (!options?.silentSuccess && response && response.status >= 200 && response.status < 300 && resource.getSuccessMessageKey) {
        let successMessageKey = resource.getSuccessMessageKey(response);
        if (successMessageKey) apiMessagesStore.setSuccess(successMessageKey);
      }

      if (!options.background) {
        apiLoadingStore.stopLoading(resource);
        // A successful request supersedes any error stored for this resource. Without this the error
        // stays in the map for the whole session and every ShowResourceContent/ShowError watching this
        // shared resource keeps rendering the stale message instead of the freshly loaded content.
        apiMessagesStore.clearError(resource);
      }
      return response;
    } catch (error) {
      if (!options.background) apiLoadingStore.stopLoading(resource);

      //ignore different errors than AxiosError
      if (!axios.isAxiosError(error)) {
        console.warn('Non-Axios error occurred:', error);
        throw error;
      }
      // Request was cancelled, do not show error message
      if (error.code === 'ERR_CANCELED') throw error;

      console.warn('Axios error:', error);
      let errorMessageKey = resource.getErrorMessageKey(error);
      if (errorMessageKey) {
        if (options.background) apiMessagesStore.showError(errorMessageKey);
        else apiMessagesStore.setError(resource, errorMessageKey);
      }
      throw error;
    }
  }

  private processParams(params?: Record<string, any>): Args | undefined {
    if (!params) return undefined;

    return Object.entries(params).reduce(
      (acc, [key, value]) => {
        if (Array.isArray(value) && key !== 'sort') {
          acc[key] = value.join(',');
        } else if (value !== undefined && value !== null) {
          acc[key] = value;
        }
        return acc;
      },
      {} as Record<string, string | number>,
    );
  }
}
